import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, Plus, Search, Edit2, Trash2, Key, 
  CheckCircle, XCircle, Shield, User, RefreshCw,
  Mail, Lock, Loader2, AlertTriangle, Settings, X,
  Filter, ShieldCheck, ShieldOff, Eye, EyeOff,
  ChevronDown, MoreHorizontal
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { permissionsAPI } from '../../services/api';
import CrossRolePermissions from './CrossRolePermissions';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SYSTEM_ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'bfr', label: 'BFR', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'gtm_sales', label: 'GTM Sales', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'gtm_sales_leader', label: 'GTM Sales Leader', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  { value: 'executive', label: 'Executive', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'cx', label: 'CX', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  { value: 'cx_leader', label: 'CX Leader', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'qc', label: 'QC', color: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200' },
  { value: 'onboarding_form', label: 'Onboarding Form', color: 'bg-rose-100 text-rose-700 border-rose-200' },
];

const ROLE_COLOR_MAP = Object.fromEntries(SYSTEM_ROLES.map(r => [r.value, r.color]));

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  leads: 'Account',
  leadAnalytics: 'Lead Analytics',
  tasks: 'Tasks',
  workflows: 'Workflows',
  reports: 'Reports',
  callHistory: 'Call History',
  callAnalytics: 'Call Analytics',
  callToppers: 'Call Toppers',
  properties: 'Properties',
  hotelLifecycle: 'Hotel Lifecycle',
  inbox: 'Inbox',
  campaigns: 'Campaigns',
  broadcast: 'Broadcast',
  chatbot: 'Chatbot',
  whatsappAnalytics: 'WA Analytics',
  wa_group_broadcast: 'Group Broadcast',
  auditHistory: 'Audit History',
  userManagement: 'User Management',
  settings: 'Settings',
  otaManagement: 'OTA Management',
  performanceInsights: 'Performance Insights',
  insights: 'Insights',
  insights_gtm: 'GTM Dashboard',
  insights_cx: 'CX Leader Dashboard',
  insights_ota: 'OTA Dashboard',
  insights_bfr: 'BFR Feedback',
  insights_qc_owner: 'QC Owner Dashboard',
  insights_executive: 'Executive Dashboard',
  insights_onboarding_form: 'Onboarding Form Dashboard',
};

const SUB_MODULE_GROUPS = {
  insights: ['insights_gtm', 'insights_cx', 'insights_ota', 'insights_bfr', 'insights_qc_owner', 'insights_executive', 'insights_onboarding_form'],
};
const SUB_MODULE_SET = new Set(Object.values(SUB_MODULE_GROUPS).flat());

const ACTIONS = ['view', 'edit', 'create', 'delete', 'action'];
const ACTION_LABELS = { view: 'View', edit: 'Edit', create: 'Create', delete: 'Delete', action: 'Action' };

// Organized module groups for the permission grid
const MODULE_GROUPS = [
  { label: 'CRM', modules: ['dashboard', 'leads', 'leadAnalytics', 'tasks', 'workflows', 'reports'] },
  { label: 'Calls', modules: ['callHistory', 'callAnalytics', 'callToppers'] },
  { label: 'Properties', modules: ['properties', 'hotelLifecycle'] },
  { label: 'WhatsApp', modules: ['inbox', 'campaigns', 'broadcast', 'chatbot', 'whatsappAnalytics', 'wa_group_broadcast'] },
  { label: 'Insights', modules: ['insights'], subModules: { insights: ['insights_gtm', 'insights_cx', 'insights_ota', 'insights_bfr', 'insights_qc_owner', 'insights_executive', 'insights_onboarding_form'] } },
  { label: 'Administration', modules: ['otaManagement', 'performanceInsights', 'auditHistory', 'userManagement', 'settings'] },
];

// Grid columns: the 4 user-facing permissions shown individually
const GRID_ACTIONS = ['view', 'create', 'edit', 'delete'];
const GRID_ACTION_LABELS = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete' };

// =================== TOGGLE SWITCH ===================
const Toggle = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={!disabled ? onChange : undefined}
    disabled={disabled}
    className={`relative inline-flex h-[22px] w-[40px] shrink-0 items-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 ${
      disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'
    } ${checked ? 'bg-teal-500' : 'bg-gray-300'}`}
    data-testid={testId}
  >
    <span className={`pointer-events-none inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
      checked ? 'translate-x-[20px]' : 'translate-x-[3px]'
    }`} />
  </button>
);

// =================== USER MANAGEMENT (MAIN) ===================
const UserManagement = () => {
  const { user: currentUser, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load users');
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setActionLoading('create');
      const response = await fetch(`${API_BASE}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(userData)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create user');
      }
      setSuccess('User created successfully');
      setShowCreateModal(false);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      setActionLoading('update');
      const response = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(userData)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update user');
      }
      setSuccess('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      setActionLoading(`delete_${userId}`);
      const response = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete user');
      }
      setSuccess('User deleted successfully');
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      setActionLoading(`status_${userId}`);
      const response = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update status');
      }
      setSuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      setActionLoading('reset');
      const response = await fetch(`${API_BASE}/api/users/${userId}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: newPassword })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to reset password');
      }
      setSuccess('Password reset successfully');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = useMemo(() => users.filter(u => {
    if (roleFilter !== 'all' && u.role?.toLowerCase() !== roleFilter) return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q);
  }), [users, searchQuery, roleFilter, statusFilter]);

  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => { setError(''); setSuccess(''); }, 5000);
      return () => clearTimeout(t);
    }
  }, [error, success]);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={48} className="text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">Only administrators can access user management.</p>
        </div>
      </div>
    );
  }

  const getRoleColor = (role) => ROLE_COLOR_MAP[role?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
  const getRoleLabel = (role) => SYSTEM_ROLES.find(r => r.value === role?.toLowerCase())?.label || role || 'User';

  const activeCount = users.filter(u => u.status === 'active').length;

  return (
    <div className="space-y-5" data-testid="user-management-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
            <p className="text-gray-500 mt-1">Manage users, roles, and access permissions</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3.5 py-2 bg-gray-50 rounded-xl border border-gray-100">
              <Users size={16} className="text-gray-400" />
              <span className="text-sm font-semibold text-gray-700" data-testid="total-user-count">{users.length}</span>
              <span className="text-xs text-gray-400">users</span>
              <span className="text-xs text-gray-300 mx-0.5">|</span>
              <span className="text-xs text-green-600 font-medium">{activeCount} active</span>
            </div>
            <button onClick={loadUsers} className="flex items-center gap-2 p-2.5 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 border border-gray-100 transition-colors" title="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium shadow-sm" data-testid="create-user-btn">
              <Plus size={16} /> Create User
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <AlertTriangle size={16} />{error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
          <CheckCircle size={16} />{success}
        </div>
      )}

      {/* Filters Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or role..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              data-testid="search-users"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white min-w-[140px]"
            data-testid="role-filter"
          >
            <option value="all">All Roles</option>
            {SYSTEM_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white min-w-[130px]"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-teal-600" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100">
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-5 py-4 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-5 py-4 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50/60 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0 ${
                        u.role?.toLowerCase() === 'admin' ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gradient-to-br from-teal-500 to-teal-600'
                      }`}>
                        {u.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{u.name}</p>
                        {u.id === currentUser?.id && <span className="text-[10px] text-teal-600 font-medium">(You)</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-600">{u.email}</span>
                  </td>
                  <td className="px-5 py-4">
                    {/* iter264 — Render every assigned role as a chip.
                        Fall back to the legacy scalar for users that
                        haven't been re-saved with a multi-role array. */}
                    <div className="flex flex-wrap gap-1" data-testid={`user-roles-${u.id}`}>
                      {(Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role]).map((r) => (
                        <span key={r || 'unknown'} className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold border ${getRoleColor(r)}`}>
                          {getRoleLabel(r)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <button
                      onClick={() => handleToggleStatus(u.id, u.status)}
                      disabled={actionLoading === `status_${u.id}` || u.id === currentUser?.id}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        u.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                          : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                      } ${u.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      title={u.id === currentUser?.id ? 'Cannot change own status' : `Click to ${u.status === 'active' ? 'deactivate' : 'activate'}`}
                      data-testid={`toggle-status-${u.id}`}
                    >
                      {actionLoading === `status_${u.id}` ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : u.status === 'active' ? (
                        <CheckCircle size={12} />
                      ) : (
                        <XCircle size={12} />
                      )}
                      {u.status === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-0.5">
                      {u.role?.toLowerCase() !== 'admin' && (
                        <button
                          onClick={() => { setSelectedUser(u); setShowPermissionModal(true); }}
                          className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Configure Access Permissions"
                          data-testid={`permissions-user-${u.id}`}
                        >
                          <Shield size={15} />
                        </button>
                      )}
                      <button
                        onClick={() => { setSelectedUser(u); setShowEditModal(true); }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit User"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit2 size={15} />
                      </button>
                      <button
                        onClick={() => { setSelectedUser(u); setShowResetPasswordModal(true); }}
                        className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Reset Password"
                        data-testid={`reset-password-${u.id}`}
                      >
                        <Key size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        disabled={actionLoading === `delete_${u.id}` || u.id === currentUser?.id}
                        className={`p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ${u.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={u.id === currentUser?.id ? 'Cannot delete self' : 'Delete User'}
                        data-testid={`delete-user-${u.id}`}
                      >
                        {actionLoading === `delete_${u.id}` ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <Users size={36} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No users found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {!loading && filteredUsers.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-400">
              Showing <span className="font-medium text-gray-600">{filteredUsers.length}</span> of {users.length} users
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && <CreateUserModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateUser} loading={actionLoading === 'create'} />}
      {showEditModal && selectedUser && <EditUserModal user={selectedUser} onClose={() => { setShowEditModal(false); setSelectedUser(null); }} onUpdate={(data) => handleUpdateUser(selectedUser.id, data)} loading={actionLoading === 'update'} />}
      {showResetPasswordModal && selectedUser && <ResetPasswordModal user={selectedUser} onClose={() => { setShowResetPasswordModal(false); setSelectedUser(null); }} onReset={(pw) => handleResetPassword(selectedUser.id, pw)} loading={actionLoading === 'reset'} />}
      {showPermissionModal && selectedUser && <PermissionConfigModal user={selectedUser} onClose={() => { setShowPermissionModal(false); setSelectedUser(null); }} onSaved={() => { setSuccess('Permissions updated'); setShowPermissionModal(false); setSelectedUser(null); }} />}

      {/* iter248 — Cross-Role Permissions summary. Read-only view of every user
          who has been granted permissions beyond their base role default. Uses
          GET /api/permissions/cross-role; does NOT modify existing overrides. */}
      <CrossRolePermissions token={token} />
    </div>
  );
};

// =================== CREATE USER MODAL ===================
const CreateUserModal = ({ onClose, onCreate, loading }) => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'bfr', status: 'active' });
  const handleSubmit = (e) => { e.preventDefault(); onCreate(formData); };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Plus size={20} className="text-teal-600" /> Create New User
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Enter full name" data-testid="create-user-name" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Enter email" data-testid="create-user-email" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Min 6 characters" data-testid="create-user-password" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" data-testid="create-user-role">
              {SYSTEM_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-medium">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="create-user-submit">
              {loading && <Loader2 size={18} className="animate-spin" />} Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =================== EDIT USER MODAL ===================
const EditUserModal = ({ user, onClose, onUpdate, loading }) => {
  // iter264 — Multi-role assignment. Existing users may only have the
  // legacy scalar `role`; hydrate `roles` from it so the UI shows the
  // right initial state without a migration. On save we send BOTH keys
  // — `roles` is the authoritative array and `role` is kept as the
  // primary (first-in-array, admin-first) for legacy compat.
  const initialRoles = Array.isArray(user.roles) && user.roles.length
    ? user.roles.map(r => (r || '').toLowerCase()).filter(Boolean)
    : [(user.role || 'user').toLowerCase()];
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    roles: initialRoles,
    status: user.status || 'active',
  });
  const [roleError, setRoleError] = useState('');

  const toggleRole = (value) => {
    setRoleError('');
    setFormData(prev => {
      const has = prev.roles.includes(value);
      const nextRoles = has ? prev.roles.filter(r => r !== value) : [...prev.roles, value];
      return { ...prev, roles: nextRoles };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.roles.length) {
      setRoleError('Assign at least one role');
      return;
    }
    // Primary role for legacy compat = "admin" if selected, else first.
    const primary = formData.roles.includes('admin') ? 'admin' : formData.roles[0];
    onUpdate({ ...formData, role: primary });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Edit2 size={20} className="text-blue-600" /> Edit User
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none" />
          </div>
          <div data-testid="edit-user-roles">
            <label className="block text-sm font-medium text-gray-700 mb-2">Roles <span className="text-gray-400 font-normal">(assign one or more)</span></label>
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
              {SYSTEM_ROLES.map(r => {
                const checked = formData.roles.includes(r.value);
                return (
                  <label
                    key={r.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none transition-colors ${checked ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                    data-testid={`edit-user-role-option-${r.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(r.value)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid={`edit-user-role-checkbox-${r.value}`}
                    />
                    <span className={`text-sm ${checked ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>{r.label}</span>
                  </label>
                );
              })}
            </div>
            {roleError && <p className="text-xs text-red-600 mt-2" data-testid="edit-user-role-error">{roleError}</p>}
            {formData.roles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="edit-user-selected-roles">
                {formData.roles.map(v => {
                  const meta = SYSTEM_ROLES.find(r => r.value === v);
                  return (
                    <span key={v} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${meta?.color || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                      {meta?.label || v}
                      <button
                        type="button"
                        onClick={() => toggleRole(v)}
                        className="ml-0.5 opacity-70 hover:opacity-100"
                        aria-label={`Remove ${meta?.label || v}`}
                        data-testid={`edit-user-remove-role-${v}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-medium">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="edit-user-save">
              {loading && <Loader2 size={18} className="animate-spin" />} Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =================== RESET PASSWORD MODAL ===================
const ResetPasswordModal = ({ user, onClose, onReset, loading }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  // Phase 19.5 — Independent visibility toggles for each field. Defaults
  // to hidden (type=password). Toggling changes input type + icon only;
  // reset logic and validation remain untouched.
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    onReset(password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
          <Key size={20} className="text-amber-600" /> Reset Password
        </h2>
        <p className="text-gray-500 text-sm mb-6">Set a new password for {user.name}</p>
        {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-4 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showNewPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required
                minLength={6}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder="Minimum 6 characters"
                data-testid="reset-pw-new-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                tabIndex={-1}
                data-testid="reset-pw-new-toggle"
              >
                {showNewPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showConfirmPwd ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                required
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder="Re-enter password"
                data-testid="reset-pw-confirm-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}
                tabIndex={-1}
                data-testid="reset-pw-confirm-toggle"
              >
                {showConfirmPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-medium">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2">
              {loading && <Loader2 size={18} className="animate-spin" />} Reset Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =================== PERMISSION GRID MATRIX MODAL ===================
const PermissionConfigModal = ({ user, onClose, onSaved }) => {
  const { token } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [roleDefault, setRoleDefault] = useState({});
  const [hasOverride, setHasOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // iter249 — module list is now discovered dynamically from the backend
  // response (role_default + effective) so any newly registered backend
  // module surfaces in the grid without a frontend code change. This is
  // the union of keys returned by GET /api/permissions/users/{id}.
  const [dynamicModules, setDynamicModules] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await permissionsAPI.getUserPermissions(user.id);
        const data = res.data;
        setRoleDefault(data.role_default || {});
        setHasOverride(data.has_override || false);
        setPermissions(data.effective || data.role_default || {});
        // Union of keys across role_default + effective + user_override so
        // even modules that are toggled OFF in defaults but present in the
        // backend registry still show up.
        const keys = new Set([
          ...Object.keys(data.role_default || {}),
          ...Object.keys(data.effective || {}),
          ...Object.keys(data.user_override || {}),
        ]);
        setDynamicModules(Array.from(keys));
      } catch (e) {
        setError('Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.id]);

  const togglePermission = (mod, action) => {
    setPermissions(prev => {
      const current = prev[mod] || {};
      const newVal = !current[action];
      // If turning off 'view', disable all other permissions for that module
      if (action === 'view' && !newVal) {
        return { ...prev, [mod]: ACTIONS.reduce((acc, a) => ({ ...acc, [a]: false }), {}) };
      }
      // If turning on any non-view permission, ensure 'view' is on too
      if (action !== 'view' && newVal && !current.view) {
        return { ...prev, [mod]: { ...current, [action]: true, view: true } };
      }
      return { ...prev, [mod]: { ...current, [action]: newVal } };
    });
  };

  const toggleFullAccess = (mod) => {
    setPermissions(prev => {
      const current = prev[mod] || {};
      const allOn = ACTIONS.every(a => current[a]);
      return { ...prev, [mod]: ACTIONS.reduce((acc, a) => ({ ...acc, [a]: !allOn }), {}) };
    });
  };

  const isFullAccess = (mod) => {
    const mp = permissions[mod] || {};
    return ACTIONS.every(a => mp[a]);
  };

  const isViewOn = (mod) => !!(permissions[mod]?.view);

  const grantAllGlobal = () => {
    const all = {};
    // iter249 — iterate over the dynamically discovered module list so
    // any new backend module is included automatically.
    dynamicModules.forEach(mod => {
      all[mod] = ACTIONS.reduce((acc, a) => ({ ...acc, [a]: true }), {});
    });
    setPermissions(all);
  };

  const revokeAllGlobal = () => {
    const all = {};
    dynamicModules.forEach(mod => {
      all[mod] = ACTIONS.reduce((acc, a) => ({ ...acc, [a]: false }), {});
    });
    setPermissions(all);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await permissionsAPI.updateUserPermissions(user.id, permissions);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save permissions');
    }
    setSaving(false);
  };

  const handleResetToDefault = async () => {
    setSaving(true);
    setError('');
    try {
      await permissionsAPI.updateUserPermissions(user.id, null);
      setPermissions(roleDefault);
      setHasOverride(false);
      onSaved();
    } catch (e) {
      setError('Failed to reset permissions');
    }
    setSaving(false);
  };

  // Count summary — iter249: use dynamic module list from backend.
  const totalModules = dynamicModules.length || Object.keys(MODULE_LABELS).length;
  const activeModules = dynamicModules.filter(m => permissions[m]?.view).length;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8"><Loader2 size={32} className="animate-spin text-teal-600" /></div>
      </div>
    );
  }

  const roleLabel = SYSTEM_ROLES.find(r => r.value === user.role?.toLowerCase())?.label || user.role;

  // iter249 — humanize an unknown module key so newly registered backend
  // modules render with a sensible label until MODULE_LABELS is updated.
  const labelFor = (mod) => {
    if (MODULE_LABELS[mod]) return MODULE_LABELS[mod];
    return String(mod)
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Modules already covered by an explicit MODULE_GROUPS entry (parent OR
  // any of its subModules). Anything left over from `dynamicModules`
  // renders under an "Other" catch-all so new backend modules show up
  // without a frontend code change.
  const coveredSet = new Set();
  MODULE_GROUPS.forEach(g => {
    (g.modules || []).forEach(m => coveredSet.add(m));
    Object.values(g.subModules || {}).forEach(list => (list || []).forEach(m => coveredSet.add(m)));
  });
  const uncoveredModules = dynamicModules.filter(m => !coveredSet.has(m));

  // Render a single permission row
  const renderRow = (mod, isSubModule = false) => {
    const viewOn = isViewOn(mod);
    const fullOn = isFullAccess(mod);

    return (
      <tr
        key={mod}
        className={`border-b border-gray-100 last:border-0 transition-colors ${isSubModule ? 'bg-gray-50/40' : 'hover:bg-gray-50/60'}`}
        data-testid={`perm-module-${mod}`}
      >
        {/* Module Name */}
        <td className={`py-3 pr-3 ${isSubModule ? 'pl-10' : 'pl-5'}`}>
          <div className="flex items-center gap-2">
            {isSubModule && <span className="text-gray-300 text-xs">&#x2514;</span>}
            <span className={`text-sm ${isSubModule ? 'text-gray-600' : 'font-medium text-gray-800'}`}>
              {labelFor(mod)}
            </span>
          </div>
        </td>
        {/* View */}
        <td className="py-3 px-3 text-center">
          <Toggle
            checked={viewOn}
            onChange={() => togglePermission(mod, 'view')}
            testId={`perm-${mod}-view`}
          />
        </td>
        {/* Create */}
        <td className="py-3 px-3 text-center">
          <Toggle
            checked={!!(permissions[mod]?.create)}
            onChange={() => togglePermission(mod, 'create')}
            disabled={!viewOn}
            testId={`perm-${mod}-create`}
          />
        </td>
        {/* Edit */}
        <td className="py-3 px-3 text-center">
          <Toggle
            checked={!!(permissions[mod]?.edit)}
            onChange={() => togglePermission(mod, 'edit')}
            disabled={!viewOn}
            testId={`perm-${mod}-edit`}
          />
        </td>
        {/* Delete */}
        <td className="py-3 px-3 text-center">
          <Toggle
            checked={!!(permissions[mod]?.delete)}
            onChange={() => togglePermission(mod, 'delete')}
            disabled={!viewOn}
            testId={`perm-${mod}-delete`}
          />
        </td>
        {/* Full Access */}
        <td className="py-3 px-3 text-center">
          <Toggle
            checked={fullOn}
            onChange={() => toggleFullAccess(mod)}
            testId={`perm-toggle-all-${mod}`}
          />
        </td>
      </tr>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="permission-config-modal">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Shield size={20} className="text-teal-600" /> Access Permissions
              </h2>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-sm text-gray-500">{user.name}</span>
                <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold border ${getRoleColorStatic(user.role)}`}>{roleLabel}</span>
                {hasOverride && <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-lg">Custom Override</span>}
                <span className="text-xs text-gray-400">{activeModules}/{totalModules} modules active</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X size={18} /></button>
          </div>
          {/* Bulk Actions */}
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={grantAllGlobal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
              data-testid="grant-all-global"
            >
              <ShieldCheck size={14} /> Grant All
            </button>
            <button
              onClick={revokeAllGlobal}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
              data-testid="revoke-all-global"
            >
              <ShieldOff size={14} /> Revoke All
            </button>
          </div>
        </div>

        {/* Grid Matrix */}
        <div className="flex-1 overflow-y-auto">
          {error && <div className="mx-6 mt-4 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">{error}</div>}

          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-white border-b-2 border-gray-200">
              <tr>
                <th className="text-left py-3 pl-5 pr-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[40%]">Module / Feature</th>
                <th className="text-center py-3 px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">View</th>
                <th className="text-center py-3 px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Create</th>
                <th className="text-center py-3 px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Edit</th>
                <th className="text-center py-3 px-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Delete</th>
                <th className="text-center py-3 px-3 text-[11px] font-semibold text-teal-600 uppercase tracking-wider">Full Access</th>
              </tr>
            </thead>
            <tbody>
              {MODULE_GROUPS.map((group) => (
                <React.Fragment key={group.label}>
                  {/* Group Header */}
                  <tr className="bg-gray-100/70">
                    <td colSpan={6} className="py-2 pl-5 pr-3">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{group.label}</span>
                    </td>
                  </tr>
                  {/* Module Rows */}
                  {group.modules.map(mod => (
                    <React.Fragment key={mod}>
                      {renderRow(mod, false)}
                      {/* Sub-modules */}
                      {group.subModules?.[mod]?.map(subMod => renderRow(subMod, true))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              {/* iter249 — Catch-all group for modules returned by the backend
                  but not yet placed in an explicit MODULE_GROUPS entry. Ensures
                  new backend modules surface in the grid without a frontend
                  code change. Hidden entirely when everything is covered. */}
              {uncoveredModules.length > 0 && (
                <React.Fragment key="__other__">
                  <tr className="bg-gray-100/70">
                    <td colSpan={6} className="py-2 pl-5 pr-3">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Other</span>
                    </td>
                  </tr>
                  {uncoveredModules.map(mod => (
                    <React.Fragment key={mod}>{renderRow(mod, false)}</React.Fragment>
                  ))}
                </React.Fragment>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50/50">
          <button
            onClick={handleResetToDefault}
            disabled={saving}
            className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
            data-testid="reset-to-role-default"
          >
            Reset to Role Default
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 font-medium text-sm transition-colors">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-medium disabled:opacity-50 flex items-center gap-2 text-sm transition-colors"
              data-testid="save-permissions-btn"
            >
              {saving && <Loader2 size={14} className="animate-spin" />} Save Permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getRoleColorStatic = (role) => ROLE_COLOR_MAP[role?.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';

export default UserManagement;
